import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/base/JsonLd';
import { getComicById, getRelated, comics } from '@/mocks/comics';

const CHAPTER_PREFIX = ['初遇', '心动', '试探', '误会', '告白', '守护', '情深', '危机', '相守', '抉择'];

export default function ComicDetail() {
  const { id } = useParams();
  const comic = getComicById(Number(id));

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title}漫画在线看 - ${comic.genre}恋爱漫画 - 桃花漫画网`;
    }
  }, [comic]);

  if (!comic) return <Navigate to="/" replace />;

  const related = getRelated(comic, 4);
  const chapters = Array.from({ length: Math.min(comic.chapters, 24) }, (_, i) => comic.chapters - i);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: `${comic.title}漫画`,
    author: { '@type': 'Person', name: comic.author },
    inLanguage: 'zh-CN',
    genre: comic.genre,
    description: comic.intro,
    url: `https://cartonplay.github.io/comic/${comic.id}`,
    isPartOf: { '@type': 'WebSite', name: '桃花漫画网', url: 'https://cartonplay.github.io/' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cartonplay.github.io/' },
      { '@type': 'ListItem', position: 2, name: `${comic.genre}漫画`, item: `https://cartonplay.github.io/category?g=${comic.genre}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <JsonLd data={schema} />
      <JsonLd data={breadcrumbSchema} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={comic.cover}
            alt=""
            className="h-full w-full scale-110 object-cover object-top opacity-20 blur-lg"
          />
          <div className="absolute inset-0 bg-background-50/80" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="mb-8 flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-500">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-primary-500">全部漫画</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category?g=${comic.genre}`} className="hover:text-primary-500">{comic.genre}漫画</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-600">{comic.title}</span>
          </nav>

          <div className="flex flex-col gap-8 md:flex-row md:items-start">
            <div className="relative mx-auto w-48 shrink-0 overflow-hidden rounded-2xl sm:w-56">
              <img
                src={comic.cover}
                alt={`${comic.title}漫画封面`}
                title={`${comic.title}漫画在线看`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
              <span className="absolute left-2 top-2 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                {comic.status}
              </span>
            </div>

            <div className="flex-1">
              <h1 className="font-display text-2xl font-bold text-foreground-900 md:text-4xl">
                {comic.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">{comic.genre}</span>
                <span>作者：{comic.author}</span>
                <span className="flex items-center gap-1 text-accent-600">
                  <i className="ri-star-fill text-xs" />
                  {comic.rating}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                    #{t}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {(
                  [
                    ['章节', `${comic.chapters}话`],
                    ['人气', comic.views],
                    ['更新', comic.updatedAt],
                    ['状态', comic.status],
                  ] as [string, string][]
                ).map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-background-100/80 px-4 py-3">
                    <dt className="text-xs text-foreground-400">{k}</dt>
                    <dd className="mt-1 text-sm font-bold text-foreground-800">{v}</dd>
                  </div>
                ))}
              </dl>

              <p className="mt-6 max-w-3xl text-sm leading-7 text-foreground-600">
                <strong className="font-bold text-foreground-800">剧情简介：</strong>
                {comic.intro}
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <button className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-book-2-line text-base" />
                  开始阅读
                </button>
                <button className="flex items-center gap-2 whitespace-nowrap rounded-full bg-background-100 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-200">
                  <i className="ri-heart-3-line text-base text-accent-500" />
                  收藏追更
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-foreground-900 md:text-2xl">
            章节列表
          </h2>
          <span className="text-xs text-foreground-400">共 {comic.chapters} 话</span>
        </div>
        <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {chapters.map((n, idx) => {
            const name = idx < 10 ? CHAPTER_PREFIX[idx % CHAPTER_PREFIX.length] : `${comic.chapters - n + 1}话`;
            return (
              <li key={n}>
                <Link
                  to={`/comic/${comic.id}`}
                  className="group flex items-center justify-between rounded-lg bg-background-100/70 px-4 py-3 text-sm text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  <span className="truncate">
                    {n} · {name}
                  </span>
                  <i className="ri-arrow-right-s-line text-base text-foreground-300 group-hover:text-primary-500" />
                </Link>
              </li>
            );
          })}
        </ul>
        <p className="mt-5 text-center text-xs text-foreground-400">
          前 {Math.min(comic.chapters, 24)} 话免费试读，登录后即可继续追更全部章节
        </p>
      </section>

      {/* Related */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="font-display text-xl font-bold text-foreground-900 md:text-2xl">
            你可能也喜欢
          </h2>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* More from catalog */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <h2 className="font-display text-xl font-bold text-foreground-900 md:text-2xl">
          浏览更多 {comic.genre}漫画
        </h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {comics
            .filter((c) => c.genre === comic.genre && c.id !== comic.id)
            .slice(0, 6)
            .map((c) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                {c.title}
              </Link>
            ))}
        </div>
      </section>
    </>
  );
}